import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fetchCatalogue, checkoutAndRedirect, itemKeyFor, formatCents, yearlySavingNote,
} from '../services/stripeBilling';
import styles from './AccessPendingScreen.module.css';

// Shown to a member whose 7-day Demo Week trial has ended with no active subscription.
// It's the paywall, and now the actual conversion point: the buttons open Stripe Checkout
// directly rather than sending them to the Account page for an admin to activate by hand.
//
// Prices come from /api/billing/catalogue, which resolves them against THIS user's price
// book, so a founding member sees their locked rate here and pays it at Checkout. The
// screen never names a Stripe price; it sends an item key and the server decides. Reuses
// the AccessPendingScreen styling for a consistent branded holding screen.
export default function TrialEndedScreen({ reason, onRecheck, syncing = false }) {
  const { user, signOut, getToken } = useAuth();
  const navigate = useNavigate();
  const [logoOk, setLogoOk] = useState(true);

  const [catalogue, setCatalogue] = useState(null);
  // Monthly by default, matching the pricing page. Opening on yearly means the first number
  // someone sees at the paywall is an annual total ($290), which reads as the price of
  // getting back in and is a much bigger ask than the $29 they were expecting. The yearly
  // saving is still advertised on the button, so nothing is hidden - it is offered rather
  // than presumed.
  const [billing, setBilling] = useState('monthly');
  const [busy, setBusy] = useState(null);             // itemKey mid-redirect
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const c = await fetchCatalogue({ token: getToken?.() });
      if (alive) setCatalogue(c);
    })();
    return () => { alive = false; };
    // getToken is deliberately not a dependency: it is not referentially stable, and the
    // token is re-read on every run anyway. Mirrors useAccessStatus.
  }, [user?.id]);

  const priceFor = (plan) => catalogue?.items?.[itemKeyFor(plan, billing)] || null;
  const monthlyFor = (plan) => catalogue?.items?.[itemKeyFor(plan, 'monthly')] || null;

  async function buy(plan) {
    const itemKey = itemKeyFor(plan, billing);
    setBusy(itemKey);
    setError(null);
    try {
      await checkoutAndRedirect(itemKey, { token: getToken?.() });
      // Navigation takes over from here; nothing after this runs on success.
    } catch (err) {
      // An existing subscriber who somehow reached the paywall belongs in the Portal,
      // where Stripe can prorate, not in a second Checkout that would bill them twice.
      if (err.code === 'already_subscribed') {
        navigate('/account?tab=billing');
        return;
      }
      setError(err.message || 'Could not start checkout. Please try again.');
      setBusy(null);
    }
  }

  // The price book's own label, so a locked rate is announced as what it is rather than
  // looking like a pricing bug to the person reading it.
  //
  // Deliberately NOT user_metadata.founding_member. That flag means "gets the bigger trial
  // allowance" and is stamped at registration, while the PRICE comes from the user's price
  // book. Driving this screen from the flag let the two disagree: a founding registrant
  // whose book assignment was missing saw a "Founding Member" banner above a public $59
  // price, the screen promising one thing and the checkout charging another. Whatever
  // actually sets the price is the only honest thing to badge.
  //
  // Both of these must stay BELOW the useState above - reading `catalogue` before its
  // declaration is a temporal dead zone error that no build step catches.
  const bookLabel = catalogue?.priceBook?.lifetimeLock ? catalogue.priceBook.label : null;
  const isFounding = !!bookLabel;

  // Two ways the trial ends - whichever comes first - so the copy matches what happened.
  const exhausted = reason === 'leads_exhausted';
  const headline = exhausted ? "You've used all your leads." : 'Your Demo Week has ended.';
  const lede = exhausted
    ? "That's all 150 of your Demo Week lead credits - nice hustle."
    : "That's a wrap on your 7-day free Solo trial - we hope RepBuddy pulled its weight.";

  return (
    <div className={styles.screen}>
      <div className={styles.inner}>
        <div className={styles.brand}>
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <span className={styles.logoMark}>R</span>
          )}
          <span className={styles.logoName}>RepBuddy</span>
        </div>

        {/* The book's own label, so a future discount cohort announces itself correctly
            instead of every locked rate being called "Founding Member". */}
        <span className={styles.eyebrow}>{bookLabel || 'Trial ended'}</span>
        <h1 className={styles.headline}>{headline}</h1>

        <p className={styles.sub}>
          {lede}
          {isFounding
            ? ' Lock in your Founding rate to keep your leads, verifications and enrichment flowing.'
            : ' Choose a plan to keep your leads, verifications and enrichment flowing.'}
        </p>

        <div className={styles.actionBox}>
          <p className={styles.trialNote}>
            Pick a plan and you're back in straight away. Your saved chats and contacts are
            waiting for you inside.
          </p>

          {bookLabel && (
            <p className={styles.trialNote} style={{ marginTop: 0 }}>
              🔒 <b>{bookLabel}</b> pricing, locked to your account.
            </p>
          )}

          {/* Monthly / yearly switch. Plain buttons so this screen keeps working even
              when it renders outside the main app shell. */}
          <div role="group" aria-label="Billing cycle" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14 }}>
            {[['monthly', 'Monthly'], ['yearly', 'Yearly']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setBilling(value)}
                aria-pressed={billing === value}
                style={{
                  padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
                  fontFamily: 'var(--font-display)', fontSize: 12,
                  border: '1px solid var(--border-bright)',
                  background: billing === value ? 'var(--border-bright)' : 'transparent',
                  color: 'var(--text-primary)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['Solo', 'Pro'].map((plan) => {
              const item = priceFor(plan);
              const monthly = monthlyFor(plan);
              const itemKey = itemKeyFor(plan, billing);
              const note = billing === 'yearly' && item && monthly
                ? yearlySavingNote(monthly.amountCents, item.amountCents)
                : null;
              return (
                <button
                  key={plan}
                  className={styles.enterBtn}
                  disabled={!item || busy !== null}
                  onClick={() => buy(plan)}
                  style={{ minWidth: 170 }}
                >
                  {busy === itemKey ? 'Opening checkout…' : (
                    <>
                      {plan}
                      {item ? ` · ${formatCents(item.amountCents)}/${billing === 'yearly' ? 'yr' : 'mo'}` : ''}
                      {note ? ` · ${note}` : ''}
                      {' →'}
                    </>
                  )}
                </button>
              );
            })}
          </div>

          {/* Only shown when the catalogue could not be read. Sending them to the account
              page is a worse experience than Checkout but it is not a dead end. */}
          {catalogue && !catalogue.available && (
            <p className={styles.trialNote}>
              We couldn't load pricing just now.{' '}
              <button
                className={styles.signout}
                onClick={() => navigate('/account?tab=billing')}
                style={{ background: 'transparent', cursor: 'pointer' }}
              >
                Open billing
              </button>
            </p>
          )}

          {error && <p className={styles.trialNote} style={{ color: 'var(--danger, #e5484d)' }}>{error}</p>}

          <p className={styles.trialNote} style={{ marginBottom: 0, opacity: 0.75 }}>
            Secure checkout by Stripe. Cancel any time.
          </p>
        </div>

        {/* The recovery path for anyone whose payment went through but whose access did
            not. `onRecheck` asks Stripe directly rather than just re-reading our own
            database, so it can actually rescue an account the webhook never reached. */}
        <div className={styles.explore}>
          <p className={styles.exploreText}>
            Already paid? We&rsquo;ll check with Stripe and unlock your account.
          </p>
          <button
            className={styles.exploreBtn}
            onClick={onRecheck}
            disabled={syncing}
            style={{ background: 'transparent', cursor: syncing ? 'default' : 'pointer', opacity: syncing ? 0.6 : 1 }}
          >
            {syncing ? 'Checking with Stripe…' : 'Refresh access'}
          </button>
        </div>

        <div className={styles.footer}>
          {user?.email && <span className={styles.who}>Signed in as {user.email}</span>}
          <span> · </span>
          <Link className={styles.signout} to="/">Back to site</Link>
          <span> · </span>
          <button className={styles.signout} onClick={signOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
}
